import React, { useRef, useCallback, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR, useXRInputSourceState } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore, PrimitiveShape, PrimitiveItem } from '../../stores/worldStore';
import { broadcastObjectAdd, broadcastObjectUpdate } from '../../services/worldBroadcast';
import { hands, handMode, pinch, type HandSide } from './hands/handTracking';
import { usePortalDraft, newPortalLink, PORTAL_W, PORTAL_H } from '../portals/portals';
import { GT_W, GT_H, GT_REST_Y } from '../games/tableLayout';
import { armedGame, getGameDef } from '../games/registry';
import { ARC_W, ARC_H, ARC_D, ARC_REST_Y, armedArcade, arcadeCabinetName, armedBoardConfig, boardBox, boardRestY } from '../games/arcade/arcadeShared';
import { useVRBuild } from './build/vrBuildState';

interface VRPlacementToolProps {
  worldId: string;
}

/**
 * VRPlacementTool — raycast from right controller, show ghost preview, trigger to place.
 * Active when activeTool === 'place' and placementShape is set.
 */
const VRPlacementTool: React.FC<VRPlacementToolProps> = ({ worldId }) => {
  const isPresenting = useXR((s) => !!s.session);
  const canBuild = useWorldStore((s) => s.canBuild);
  const activeTool = useWorldStore((s) => s.activeTool);
  const placementShape = useWorldStore((s) => s.placementShape);
  const panelType = useWorldStore(s => s.vrPanelType);
  const contentSpec = useVRBuild((s) => s.placing);
  const xrOrigin = useXR((s) => s.origin);
  // Hands: the pinch that picked the item is still held — wait for a new one.
  const pinchPrev = useRef<Record<HandSide, boolean>>({ left: true, right: true });
  const ghostRef = useRef<THREE.Mesh>(null);
  const rayRef = useRef<THREE.Line>(null);
  const raycaster = useRef(new THREE.Raycaster());
  const { scene, camera } = useThree();
  const hitPoint = useRef(new THREE.Vector3());
  const hasHit = useRef(false);
  const triggerPrev = useRef(false);

  // Try to get right controller state
  let rightCtrl: any = null;
  try {
    rightCtrl = useXRInputSourceState('controller', 'right');
  } catch (e) {
    // Hook may fail if no controller connected
  }

  // Do not place using the same trigger press that selected the menu item.
  useEffect(() => { triggerPrev.current = true; pinchPrev.current = { left: true, right: true }; }, [placementShape, panelType, contentSpec]);
  const confirmPlace = useCallback(() => {
    if (!hasHit.current || (!placementShape && !panelType && !contentSpec)) return;
    if (contentSpec) {
      // A library / Studio model: rest it on the surface the ray hit.
      const store = useWorldStore.getState();
      const id = `content-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const eye = camera.getWorldPosition(new THREE.Vector3());
      const p = hitPoint.current;
      const item: any = {
        url: contentSpec.url, type: 'model', position: [p.x, p.y - (contentSpec.minY ?? 0), p.z],
        scale: 1, rotation: [0, Math.atan2(eye.x - p.x, eye.z - p.z), 0], visible: true, isLocked: false,
        filename: contentSpec.filename, uploadedAt: Date.now(),
        ...(contentSpec.autoFit ? { autoFit: true } : {}),
        ...(contentSpec.thumbnailUrl ? { thumbnailUrl: contentSpec.thumbnailUrl } : {}),
      };
      store.addContentItem(id, item);
      broadcastObjectAdd(worldId, 'content', id, item);
      store.setSelectedObjectId(id);
      if (contentSpec.autoFit && Math.abs(p.y) > .01) {
        // autoFit rests the model at y=0 once it has measured it; lift it back
        // onto the surface it was placed on (a stage, an upper floor…).
        const surfaceY = p.y;
        const unsub = useWorldStore.subscribe((st) => {
          const it = st.contentItems[id];
          if (!it) { unsub(); return; }
          if (it.autoFit) return;
          unsub();
          const pos: [number, number, number] = [it.position[0], it.position[1] + surfaceY, it.position[2]];
          st.updateContentItem(id, { position: pos });
          broadcastObjectUpdate(worldId, 'content', id, { position: pos });
        });
        setTimeout(unsub, 20000);
      }
      useVRBuild.getState().setPlacing(null);
      return;
    }
    const shape = placementShape as PrimitiveShape;
    const store = useWorldStore.getState();
    const snap = store.snapSettings;
    let pos: [number, number, number] = [hitPoint.current.x, hitPoint.current.y + (panelType ? (panelType === 'browser' ? 1.7 : 1.4) : shape === 'portal' ? PORTAL_H / 2 : shape === 'gametable' ? GT_REST_Y : shape === 'arcade' ? ARC_REST_Y : shape === 'leaderboard' ? boardRestY(armedBoardConfig()) : .5), hitPoint.current.z];
    if (snap.grid) {
      pos = [
        Math.round(pos[0] / snap.gridSize) * snap.gridSize,
        shape === 'portal' || shape === 'gametable' || shape === 'arcade' || shape === 'leaderboard' ? pos[1] : Math.round(pos[1] / snap.gridSize) * snap.gridSize, // a portal stands on the floor
        Math.round(pos[2] / snap.gridSize) * snap.gridSize,
      ];
    }

    if (panelType) {
      const id = `screen-${crypto.randomUUID()}`;
      const eye = camera.getWorldPosition(new THREE.Vector3());
      const position: [number, number, number] = [pos[0], hitPoint.current.y + (panelType === 'browser' ? 1.7 : 1.4), pos[2]];
      const item = { type: panelType, position, rotation: [0, Math.atan2(eye.x - pos[0], eye.z - pos[2]), 0] as [number, number, number], scale: 1, visible: true, isLocked: false, uploadedAt: Date.now() };
      store.addScreenProp(id, item);
      broadcastObjectAdd(worldId, 'screenprop', id, item);
      store.setSelectedObjectId(null);
      store.setActiveTool('select');
      store.setVrPanelType(null);
      return;
    }
    const id = `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    // Portals: first pinch places the entrance, the second its exit; both
    // face you, so you walk into the front of the one you just placed.
    const draft = usePortalDraft.getState();
    const isPortal = shape === 'portal';
    const isTable = shape === 'gametable';
    const game = isTable ? armedGame() : undefined; // which game the VR menu armed
    const isArcade = shape === 'arcade';
    const isBoard = shape === 'leaderboard';
    const arcadeGame = isArcade ? armedArcade() ?? undefined : undefined;
    const eyeA = isArcade || isBoard ? camera.getWorldPosition(new THREE.Vector3()) : null;
    const portalLink = isPortal ? (draft.pending?.link ?? newPortalLink()) : undefined;
    const eyeP = camera.getWorldPosition(new THREE.Vector3());
    const item: PrimitiveItem = {
      shape,
      name: isPortal ? (draft.pending ? 'Portal (exit)' : 'Portal (entrance)') : isTable ? `${getGameDef(game)?.label ?? 'Game'} Table` : isArcade ? arcadeCabinetName(arcadeGame) : isBoard ? 'Leaderboard' : shape.charAt(0).toUpperCase() + shape.slice(1),
      position: pos,
      // Portals and arcade cabinets face you as they're placed.
      rotation: isPortal ? [0, Math.atan2(eyeP.x - pos[0], eyeP.z - pos[2]), 0] : eyeA ? [0, Math.atan2(eyeA.x - pos[0], eyeA.z - pos[2]), 0] : [0, 0, 0],
      scale: [1, 1, 1],
      color: isPortal ? '#8b5cf6' : isTable ? '#0f5a3c' : '#888888',
      ...(isPortal ? { portalLink } : {}),
      ...(isTable ? { game } : {}),
      ...(isArcade && arcadeGame ? { arcadeGame } : {}),
      ...(isBoard ? { board: { ...armedBoardConfig() } } : {}),
      texturePreset: null,
      textureUrl: null,
      pbrMaps: null,
      textureScale: 1,
      visible: true,
      isLocked: false,
      collidable: false,
      opacity: 1,
      metalness: 0,
      roughness: 0.5,
      colliderOnly: false,
      ...(shape === 'text' ? { text: 'Hello World', fontSize: 1 } : {}),
    };
    store.addPrimitive(id, item);
    broadcastObjectAdd(worldId, 'primitive', id, item);
    store.setSelectedObjectId(id);
    if (isPortal && !draft.pending) { draft.set({ link: portalLink!, id }); return; } // now place the exit
    if (isPortal) draft.set(null);
    // Exit placement mode after placing one object
    store.setActiveTool('select');
    store.setPlacementShape(null);
  }, [worldId, placementShape, panelType, contentSpec, camera]);

  useFrame(() => {
    const active = (activeTool === 'place' && (placementShape || panelType)) || !!contentSpec;
    if (!active || !ghostRef.current) {
      if (ghostRef.current) ghostRef.current.visible = false;
      if (rayRef.current) rayRef.current.visible = false;
      return;
    }

    // Try to get ray from controller, fall back to camera gaze
    let origin = new THREE.Vector3();
    let direction = new THREE.Vector3();

    const ctrlObject = rightCtrl?.object;
    // Tracked hands: aim with the hand that's free (right first).
    const handSide: HandSide | null = handMode.active ? (hands.right.present ? 'right' : hands.left.present ? 'left' : null) : null;
    if (handSide && xrOrigin) {
      const h = hands[handSide];
      xrOrigin.updateMatrixWorld();
      origin.copy(h.rayOrigin).applyMatrix4(xrOrigin.matrixWorld);
      direction.copy(h.rayDir).transformDirection(xrOrigin.matrixWorld);
    } else if (ctrlObject) {
      ctrlObject.getWorldPosition(origin);
      direction.set(0, 0, -1).applyQuaternion(ctrlObject.getWorldQuaternion(new THREE.Quaternion()));
    } else {
      // Fallback: use camera gaze (for hand tracking or missing controller)
      camera.getWorldPosition(origin);
      camera.getWorldDirection(direction);
    }

    raycaster.current.set(origin, direction);
    // Only hit ground meshes, skip the invisible teleport plane and tiny objects
    const intersects = raycaster.current.intersectObjects(scene.children, true)
      .filter(h => {
        const obj = h.object;
        // Skip teleport target (invisible mesh at y=-0.49)
        if (!obj.visible && !obj.userData?.collidable) return false;
        return obj.name === 'ground' || obj.userData?.collidable ||
               (obj.parent && obj.parent.name === 'ground');
      });

    if (intersects.length > 0 && intersects[0].distance > 0.5) {
      // Place at raycast hit, but only if far enough away (not at feet)
      hitPoint.current.copy(intersects[0].point);
      ghostRef.current.position.set(hitPoint.current.x, hitPoint.current.y + (panelType ? (panelType === 'browser' ? 1.7 : 1.4) : contentSpec ? .4 : .5), hitPoint.current.z);
      ghostRef.current.visible = true;
      hasHit.current = true;
    } else {
      // No valid ground hit — place 3m in front of controller/camera at ground level
      const forward = direction.clone();
      forward.y = 0;
      forward.normalize();
      hitPoint.current.copy(origin).addScaledVector(forward, 3);
      hitPoint.current.y = 0;
      ghostRef.current.position.set(hitPoint.current.x, panelType ? (panelType === 'browser' ? 1.7 : 1.4) : .5, hitPoint.current.z);
      ghostRef.current.visible = true;
      hasHit.current = true;
    }

    if (panelType) {
      const eye = camera.getWorldPosition(new THREE.Vector3());
      ghostRef.current.rotation.y = Math.atan2(eye.x - hitPoint.current.x, eye.z - hitPoint.current.z);
    }
    // Draw ray line
    if (rayRef.current) {
      const geom = rayRef.current.geometry as THREE.BufferGeometry;
      const positions = geom.attributes.position;
      positions.setXYZ(0, origin.x, origin.y, origin.z);
      const end = ghostRef.current.position;
      positions.setXYZ(1, end.x, end.y, end.z);
      positions.needsUpdate = true;
      rayRef.current.visible = true;
    }

    // Trigger press = place (controller button or hand pinch)
    const gamepad = rightCtrl?.inputSource?.gamepad;
    const triggerPressed = gamepad?.buttons?.[0]?.pressed || false;
    if (triggerPressed && !triggerPrev.current && hasHit.current) {
      confirmPlace();
    }
    triggerPrev.current = triggerPressed;
    if (handSide) {
      // Shared pinch filter: hysteresis + grace, and a pinch another tool used doesn't count.
      const pinched = pinch[handSide].held;
      if (pinched && !pinchPrev.current[handSide] && pinch[handSide].free && hasHit.current) { pinch[handSide].consume(); confirmPlace(); }
      pinchPrev.current[handSide] = pinched;
    }
  });

  if (!isPresenting || !canBuild || (!contentSpec && (activeTool !== 'place' || (!placementShape && !panelType)))) return null;

  // Pick geometry based on shape
  const getGhostGeometry = () => {
    if (contentSpec) return <boxGeometry args={[.8, .8, .8]} />;
    if (panelType) return <boxGeometry args={[4.16, panelType === 'browser' ? 3.16 : 2.41, .12]} />;
    switch (placementShape as PrimitiveShape) {
      case 'sphere': return <sphereGeometry args={[0.5, 16, 16]} />;
      case 'cylinder': return <cylinderGeometry args={[0.5, 0.5, 1, 16]} />;
      case 'cone': return <coneGeometry args={[0.5, 1, 16]} />;
      case 'torus': return <torusGeometry args={[0.4, 0.15, 8, 16]} />;
      case 'plane': return <planeGeometry args={[1, 1]} />;
      case 'portal': return <boxGeometry args={[PORTAL_W, PORTAL_H, PORTAL_W]} />;
      case 'gametable': { const fp = getGameDef(armedGame())?.footprint; return <boxGeometry args={[fp?.w ?? GT_W, GT_H, fp?.d ?? GT_W]} />; }
      case 'arcade': return <boxGeometry args={[ARC_W, ARC_H, ARC_D]} />;
      case 'leaderboard': return <boxGeometry args={boardBox(armedBoardConfig())} />;
      default: return <boxGeometry args={[1, 1, 1]} />;
    }
  };

  return (
    <>
      {/* Ghost preview — matches selected shape */}
      <mesh ref={ghostRef} visible={false}>
        {getGhostGeometry()}
        <meshBasicMaterial color="#00c2b3" transparent opacity={0.35} wireframe />
      </mesh>

      {/* Raycast line */}
      <line ref={rayRef as any} visible={false}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[new Float32Array(6), 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#00c2b3" transparent opacity={0.5} />
      </line>
    </>
  );
};

export default VRPlacementTool;
